import type { JSX } from "preact";
import type { AcUnits, GeneratorFuel, HeatingFuel, LargeLoad } from "@ks-gen/shared";
import type { StepProps } from "../app";
import { ContactExpert } from "../ContactExpert";
import {
  BoltIcon, DotsIcon, DryerIcon, EvChargerIcon, FlameIcon, GasMeterIcon,
  HotTubIcon, NoneIcon, OilIcon, PropaneIcon, PoolPumpIcon, RangeIcon,
  SnowflakeIcon, WaterHeaterIcon, WellPumpIcon,
} from "./loadIcons";

const HEATING: { id: HeatingFuel; label: string; icon: JSX.Element }[] = [
  { id: "natural_gas", label: "Natural gas", icon: <FlameIcon /> },
  { id: "propane", label: "Propane", icon: <PropaneIcon /> },
  { id: "oil", label: "Oil", icon: <OilIcon /> },
  { id: "electric", label: "Electric", icon: <BoltIcon /> },
  { id: "other", label: "Other", icon: <DotsIcon /> },
];

// Central-AC tonnage options. Mirrors the Generac sizer's per-ton cooling loads.
const AC_TONS: { tons: number; label: string; icon: JSX.Element }[] = [
  { tons: 0, label: "None", icon: <NoneIcon /> },
  { tons: 2, label: "2 ton", icon: <SnowflakeIcon /> },
  { tons: 3, label: "3 ton", icon: <SnowflakeIcon /> },
  { tons: 4, label: "4 ton", icon: <SnowflakeIcon /> },
  { tons: 6, label: "5+ ton", icon: <SnowflakeIcon /> },
];

// Large loads. Well pump is a separate boolean field but shares the grid.
const LOADS: { id: LargeLoad; label: string; icon: JSX.Element }[] = [
  { id: "electric_range", label: "Electric range", icon: <RangeIcon /> },
  { id: "electric_dryer", label: "Electric dryer", icon: <DryerIcon /> },
  { id: "electric_water_heater", label: "Water heater", icon: <WaterHeaterIcon /> },
  { id: "ev_charger", label: "EV charger", icon: <EvChargerIcon /> },
  { id: "hot_tub", label: "Hot tub", icon: <HotTubIcon /> },
  { id: "pool_pump", label: "Pool pump", icon: <PoolPumpIcon /> },
];

const GEN_FUEL: { id: GeneratorFuel; label: string; note?: string; icon: JSX.Element }[] = [
  { id: "propane", label: "Propane", note: "recommended", icon: <PropaneIcon /> },
  { id: "natural_gas", label: "Natural gas", icon: <FlameIcon /> },
];

// Electrical service amperage. "" = "not sure" -> gates whole-home to an assessment.
const SERVICE_AMPS: { value: string; label: string }[] = [
  { value: "", label: "I'm not sure" },
  { value: "100", label: "100 A" },
  { value: "150", label: "150 A" },
  { value: "200", label: "200 A+" },
];

/** Keep the legacy 0|1|2 acUnits field in sync with tonnage for the API contract. */
function tonsToAcUnits(tons: number): AcUnits {
  if (tons <= 0) return 0;
  if (tons <= 4) return 1;
  return 2;
}

/** One selectable card. Wraps a real radio/checkbox so it stays keyboard + SR operable. */
function Choice(props: {
  type: "radio" | "checkbox";
  name?: string;
  checked: boolean;
  onSelect: () => void;
  label: string;
  note?: string;
  icon?: JSX.Element;
}) {
  return (
    <label class={`choice${props.checked ? " selected" : ""}`}>
      <input
        type={props.type}
        name={props.name}
        checked={props.checked}
        onChange={props.onSelect}
      />
      {props.icon}
      <span>
        {props.label}
        {props.note && <small>{props.note}</small>}
      </span>
    </label>
  );
}

export function Step2Loads({ state, update, next, stepName }: StepProps) {
  const toggleLoad = (id: LargeLoad) =>
    update({
      largeLoads: state.largeLoads.includes(id)
        ? state.largeLoads.filter((l) => l !== id)
        : [...state.largeLoads, id],
    });

  const setTons = (tons: number) =>
    update({ coolingTons: tons, acUnits: tonsToAcUnits(tons) });

  const valid = state.sqft != null && state.sqft > 0 && state.heatingFuel != null;

  return (
    <section class="step">
      <h2>Let's find the right backup power for your home</h2>
      <p class="hint">Answer a few quick questions and we'll size a system that fits, using the same NEC load method the pros use. It only takes a minute.</p>

      <label>Approx. square footage
        <input type="number" min={0} inputMode="numeric" value={state.sqft ?? ""} onInput={(e) => update({ sqft: Number(e.currentTarget.value) || null })} />
        <span class="field-hint">Pre-filled with a typical home — adjust if you know it.</span>
      </label>

      <fieldset>
        <legend>Primary heating fuel</legend>
        <div class="choice-grid cols">
          {HEATING.map((h) => (
            <Choice
              key={h.id}
              type="radio"
              name="heat"
              checked={state.heatingFuel === h.id}
              onSelect={() => update({ heatingFuel: h.id })}
              label={h.label}
              icon={h.icon}
            />
          ))}
        </div>
      </fieldset>

      <fieldset>
        <legend>Central air conditioning</legend>
        <div class="choice-grid cols">
          {AC_TONS.map((o) => (
            <Choice
              key={o.tons}
              type="radio"
              name="ac"
              checked={state.coolingTons === o.tons}
              onSelect={() => setTons(o.tons)}
              label={o.label}
              icon={o.icon}
            />
          ))}
        </div>
      </fieldset>

      <fieldset>
        <legend>Other large electric loads <span class="field-hint" style="display:inline; font-weight:400;">(tap all that apply)</span></legend>
        <div class="choice-grid wide">
          <Choice
            type="checkbox"
            checked={state.wellPump}
            onSelect={() => update({ wellPump: !state.wellPump })}
            label="Well pump"
            icon={<WellPumpIcon />}
          />
          {LOADS.map((l) => (
            <Choice
              key={l.id}
              type="checkbox"
              checked={state.largeLoads.includes(l.id)}
              onSelect={() => toggleLoad(l.id)}
              label={l.label}
              icon={l.icon}
            />
          ))}
        </div>
      </fieldset>

      <fieldset>
        <legend>Generator fuel</legend>
        <div class="choice-grid cols">
          {GEN_FUEL.map((f) => (
            <Choice
              key={f.id}
              type="radio"
              name="genfuel"
              checked={state.generatorFuel === f.id}
              onSelect={() => update({ generatorFuel: f.id })}
              label={f.label}
              note={f.note}
              icon={f.icon}
            />
          ))}
        </div>
        {state.generatorFuel === "natural_gas" && (
          <div class="choice-grid pills">
            <Choice
              type="checkbox"
              checked={state.gasMainAvailable}
              onSelect={() => update({ gasMainAvailable: !state.gasMainAvailable })}
              label="There's a natural-gas main at the property"
              icon={<GasMeterIcon />}
            />
            {!state.gasMainAvailable && (
              <Choice
                type="checkbox"
                checked={state.willingToSetPropaneTank}
                onSelect={() => update({ willingToSetPropaneTank: !state.willingToSetPropaneTank })}
                label="Open to a propane tank if gas isn't available"
                icon={<PropaneIcon />}
              />
            )}
          </div>
        )}
      </fieldset>

      <fieldset>
        <legend>Electrical service size</legend>
        <div class="choice-grid pills">
          {SERVICE_AMPS.map((o) => (
            <Choice
              key={o.value}
              type="radio"
              name="amps"
              checked={(state.serviceAmps == null ? "" : String(state.serviceAmps)) === o.value}
              onSelect={() => update({ serviceAmps: o.value === "" ? null : Number(o.value) })}
              label={o.label}
            />
          ))}
        </div>
        <span class="field-hint">On the main breaker in your panel. Not sure is fine — we'll confirm on site.</span>
      </fieldset>

      <div class="actions">
        <ContactExpert stepName={stepName} submissionId={state.submissionId} />
        <button class="primary" disabled={!valid} onClick={next}>Continue</button>
      </div>
    </section>
  );
}
